import {
  CUSTOMER_LOGO_LINK_SOURCES,
  CustomerLogoLink,
  CustomerLogoLinkSource,
} from "../../../core/models/entities/CustomerLogoLink";
import { CustomerLogoLinkDto } from "../responses/CustomerLogoAttachResultDto";

/**
 * Normalises the answer of GET organizations/{orgId}/logos/attach into
 * CustomerLogoLink[]:
 *  - the OLD form (API sub-flag CustomerLogoLinkSourceEnabled off): int[] of
 *    the manual marker ids -> one 'manual', removable link per id (exactly
 *    what the section treated them as before v2);
 *  - the NEW form: CustomerLogoLinkDto[] -> as sent; an unknown source code
 *    is kept visible as 'linked' (source unknown), and a missing `removable`
 *    falls back to the source rule (manual/assigned/linked may be removed).
 * Anything that is not an array, and every item that is neither a finite
 * number nor an object with a numeric logoId, is dropped - never thrown at
 * the screen.
 */
export function normalizeLinks(payload: unknown): CustomerLogoLink[] {
  if (!Array.isArray(payload)) return [];
  const links: CustomerLogoLink[] = [];
  for (const item of payload as unknown[]) {
    if (typeof item === "number") {
      if (Number.isFinite(item)) {
        links.push({ logoId: item, source: "manual", removable: true });
      }
      continue;
    }
    if (item === null || typeof item !== "object") continue;
    const dto = item as Partial<CustomerLogoLinkDto>;
    if (typeof dto.logoId !== "number" || !Number.isFinite(dto.logoId)) continue;
    const source = asSource(dto.source);
    links.push({
      logoId: dto.logoId,
      source,
      createdUtc: typeof dto.createdUtc === "string" ? dto.createdUtc : null,
      createdByName:
        typeof dto.createdByName === "string" && dto.createdByName.trim()
          ? dto.createdByName.trim()
          : null,
      removable:
        typeof dto.removable === "boolean"
          ? dto.removable
          : source === "manual" || source === "assigned" || source === "linked",
      notRemovableReason:
        typeof dto.notRemovableReason === "string" && dto.notRemovableReason
          ? dto.notRemovableReason
          : null,
    });
  }
  return links;
}

function asSource(value: unknown): CustomerLogoLinkSource {
  return typeof value === "string" &&
    (CUSTOMER_LOGO_LINK_SOURCES as readonly string[]).includes(value)
    ? (value as CustomerLogoLinkSource)
    : "linked";
}
